import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Upload, X, Send, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useQuoteCart } from '../context/QuoteCartContext';
import { useAuth } from '../context/AuthContext';
import { quotesAPI, uploadAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export function QuotePage() {
  const { t, language } = useLanguage();
  const { items, removeItem, updateItem, clearCart } = useQuoteCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    company: user?.company || '',
    contact_name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    sector: '',
    message: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const sectors = [
    { value: 'hotel', label: t('sector.hotel') },
    { value: 'restaurant', label: t('sector.restaurant') },
    { value: 'real_estate', label: t('sector.real_estate') },
    { value: 'event', label: t('sector.event') },
    { value: 'retail', label: t('sector.retail') },
    { value: 'corporate', label: t('sector.corporate') },
    { value: 'other', label: t('sector.other') },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const res = await uploadAPI.upload(file);
        setAttachments(prev => [...prev, {
          file_id: res.data.file_id,
          filename: res.data.filename
        }]);
      }
      toast.success(language === 'fr' ? 'Fichier(s) téléchargé(s)' : 'File(s) uploaded');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors du téléchargement' : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (fileId) => {
    setAttachments(prev => prev.filter(a => a.file_id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      toast.error(language === 'fr' ? 'Veuillez ajouter des produits' : 'Please add products');
      return;
    }

    if (!formData.company || !formData.contact_name || !formData.email || !formData.phone || !formData.sector) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const quoteItems = items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        variant_id: item.variant_id,
        quantity: item.quantity,
        nfc_option: item.nfc_option,
        printing_option: item.printing_option,
        notes: item.notes
      }));

      await quotesAPI.create({
        ...formData,
        items: quoteItems,
        attachments: attachments.map(a => a.file_id)
      });

      setSubmitted(true);
      clearCart();
      toast.success(t('quote.success'));
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-20 text-center" data-testid="quote-success">
        <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h1 className="text-3xl font-bold mb-4 font-['Outfit']">
          {language === 'fr' ? 'Demande envoyée !' : 'Request submitted!'}
        </h1>
        <p className="text-muted-foreground mb-8">
          {language === 'fr' 
            ? 'Nous avons bien reçu votre demande de devis. Notre équipe vous contactera dans les plus brefs délais.'
            : 'We have received your quote request. Our team will contact you shortly.'}
        </p>
        <div className="flex gap-4 justify-center">
          <Button onClick={() => navigate('/catalogue')} variant="outline" className="rounded-none">
            {t('hero.cta.catalog')}
          </Button>
          {user && (
            <Button onClick={() => navigate('/dashboard')} className="rounded-none bg-[#1F2937]">
              {t('nav.dashboard')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-8 md:py-12" data-testid="quote-page">
      <h1 className="text-3xl md:text-4xl font-bold mb-8 font-['Outfit']">
        {t('quote.title')}
      </h1>

      <div className="grid lg:grid-cols-3 gap-12">
        {/* Products List */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-border">
            <div className="p-4 border-b border-border bg-secondary">
              <h2 className="font-semibold">
                {t('quote.products')} ({items.length})
              </h2>
            </div>
            
            {items.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p className="mb-4">{language === 'fr' ? 'Aucun produit sélectionné' : 'No products selected'}</p>
                <Button onClick={() => navigate('/catalogue')} variant="outline" className="rounded-none">
                  {t('hero.cta.catalog')}
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {items.map((item) => (
                  <div key={item.id} className="p-4 flex gap-4" data-testid={`quote-item-${item.id}`}>
                    <div className="w-20 h-20 bg-secondary flex-shrink-0">
                      {item.product_image ? (
                        <img src={item.product_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs font-mono">
                          NFC
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold line-clamp-1">
                        {language === 'fr' ? item.product_name : item.product_name_en}
                      </h3>
                      {item.variant_name && (
                        <p className="text-sm text-muted-foreground">{item.variant_name}</p>
                      )}
                      {item.nfc_option && (
                        <p className="text-xs text-muted-foreground font-mono">{item.nfc_option.chip_type}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-none"
                          onClick={() => updateItem(item.id, { quantity: Math.max(1, item.quantity - 1) })}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, { quantity: parseInt(e.target.value) || 1 })}
                          className="w-16 h-8 text-center rounded-none"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-none"
                          onClick={() => updateItem(item.id, { quantity: item.quantity + 1 })}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attachments */}
          <div className="border border-border">
            <div className="p-4 border-b border-border bg-secondary">
              <h2 className="font-semibold">{t('quote.attachments')}</h2>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-2 mb-4">
                {attachments.map((file) => (
                  <div key={file.file_id} className="flex items-center gap-2 px-3 py-2 bg-secondary text-sm">
                    <span className="truncate max-w-[200px]">{file.filename}</span>
                    <button onClick={() => removeAttachment(file.file_id)} className="text-muted-foreground hover:text-destructive">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-border cursor-pointer hover:border-[#1F2937] transition-colors">
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {uploading ? (language === 'fr' ? 'Téléchargement...' : 'Uploading...') : (language === 'fr' ? 'Ajouter des fichiers (Logo, PDF, AI, SVG)' : 'Add files (Logo, PDF, AI, SVG)')}
                </span>
                <input
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.svg,.pdf,.ai"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="file-upload-input"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Quote Form */}
        <div className="lg:col-span-1">
          <form onSubmit={handleSubmit} className="border border-border sticky top-24">
            <div className="p-4 border-b border-border bg-secondary">
              <h2 className="font-semibold">{language === 'fr' ? 'Vos coordonnées' : 'Your details'}</h2>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label htmlFor="company">{t('quote.company')} *</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="rounded-none mt-1"
                  data-testid="quote-company-input"
                />
              </div>
              <div>
                <Label htmlFor="contact_name">{t('quote.name')} *</Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleInputChange}
                  required
                  className="rounded-none mt-1"
                  data-testid="quote-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">{t('quote.email')} *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="rounded-none mt-1"
                  data-testid="quote-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">{t('quote.phone')} *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="rounded-none mt-1"
                  data-testid="quote-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="sector">{t('quote.sector')} *</Label>
                <Select value={formData.sector} onValueChange={(value) => setFormData(prev => ({ ...prev, sector: value }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="quote-sector-select">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map((sector) => (
                      <SelectItem key={sector.value} value={sector.value}>
                        {sector.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="message">{t('quote.message')}</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="rounded-none mt-1 resize-none"
                  data-testid="quote-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || items.length === 0}
                className="w-full rounded-none bg-[#1F2937] hover:bg-[#1F2937]/90 uppercase tracking-widest text-xs py-6 h-auto"
                data-testid="submit-quote-btn"
              >
                {submitting ? (
                  <span>{language === 'fr' ? 'Envoi...' : 'Sending...'}</span>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('quote.submit')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default QuotePage;
